import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Table,
  Button,
  Space,
  Form,
  Input,
  InputNumber,
  Select,
  message,
  Popconfirm,
  Card,
  Row,
  Col,
  Typography,
  Tag,
  DatePicker,
  Modal,
  Upload,
  Badge,
  Tooltip,
  Image,
} from 'antd';
import {
  DeleteOutlined,
  FileExcelOutlined,
  CheckOutlined,
  CloseOutlined,
  EditOutlined,
  PlusOutlined,
  ArrowLeftOutlined,
  CalendarOutlined,
  PaperClipOutlined,
  UploadOutlined,
  FilePdfOutlined as FilePdfIcon,
  EyeOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import apiService from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { COLOR_PRIMARY } from '../../utils/colors';

const { Title, Text } = Typography;
const { Option } = Select;

const VAN_TYPES = ['LWB 4.2', 'Luton', 'Large Luton', 'Curtain side', 'Car'];
const EXCLUDED_DRIVERS = ['external', 'shafiq', 'kaler', 'ravinder singh'];

// 15-minute increments 04:00 – 23:45
const TIME_OPTIONS = (() => {
  const times = [];
  for (let h = 4; h < 24; h++) {
    for (let m = 0; m < 60; m += 15) {
      times.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    }
  }
  return times;
})();

// ─── Date list view ───────────────────────────────────────────────────────────

const DateListView = ({ onSelectDate }) => {
  const { isOpcUser } = useAuth();
  const [dateSummaries, setDateSummaries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchPostcode, setSearchPostcode] = useState('');
  const [searchRef, setSearchRef] = useState('');
  const [exportLoading, setExportLoading] = useState(false);
  const [viewMonth, setViewMonth] = useState(dayjs());

  const today = dayjs().format('YYYY-MM-DD');

  useEffect(() => {
    const fetchDates = async () => {
      try {
        setLoading(true);
        const startDate = viewMonth.startOf('month').format('YYYY-MM-DD');
        const endDate   = viewMonth.endOf('month').format('YYYY-MM-DD');
        const res = await apiService.dailyRuns.getDates({ startDate, endDate });
        const data = res.data || [];
        const isCurrentMonth = viewMonth.isSame(dayjs(), 'month');
        const hasToday = data.some(d => d.date === today);
        const list = (isCurrentMonth && !hasToday)
          ? [{ date: today, count: 0, total: 0, net: 0 }, ...data]
          : data;
        setDateSummaries(list);
      } catch {
        message.error('Failed to load dates');
      } finally {
        setLoading(false);
      }
    };
    fetchDates();
  }, [today, viewMonth]);

  // Debounced search — fires 400ms after the user stops typing
  useEffect(() => {
    const postcode = searchPostcode.trim();
    const ref = searchRef.trim();

    if (!postcode && !ref) {
      setSearchResults(null);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setSearchLoading(true);
        if (postcode) {
          const [pickupRes, dropRes] = await Promise.all([
            apiService.dailyRuns.getAll({ pickupPostcode: postcode, ...(ref && { reference: ref }) }),
            apiService.dailyRuns.getAll({ dropOffPostcode: postcode, ...(ref && { reference: ref }) }),
          ]);
          const combined = [...(pickupRes.data || []), ...(dropRes.data || [])];
          const seen = new Set();
          setSearchResults(combined.filter(r => { if (seen.has(r._id)) return false; seen.add(r._id); return true; }));
        } else {
          const res = await apiService.dailyRuns.getAll({ reference: ref });
          setSearchResults(res.data || []);
        }
      } catch {
        message.error('Search failed');
      } finally {
        setSearchLoading(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [searchPostcode, searchRef]);

  const handleClearSearch = () => {
    setSearchPostcode('');
    setSearchRef('');
    setSearchResults(null);
  };

  const handleExportMonth = async () => {
    try {
      setExportLoading(true);
      const startDate = viewMonth.startOf('month').format('YYYY-MM-DD');
      const endDate   = viewMonth.endOf('month').format('YYYY-MM-DD');
      const res = await apiService.dailyRuns.export({ startDate, endDate });
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `daily-runs-${viewMonth.format('MMM-YYYY')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      message.error('Export failed');
    } finally {
      setExportLoading(false);
    }
  };

  const columns = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: (d) => {
        const isToday = d === today;
        return (
          <Space>
            <Text strong>{dayjs(d).format('ddd DD MMM YYYY')}</Text>
            {isToday && <Tag color="blue">Today</Tag>}
          </Space>
        );
      },
    },
    {
      title: 'Runs',
      dataIndex: 'count',
      key: 'count',
      width: 80,
      align: 'center',
      render: (v) => <Text type={v === 0 ? 'secondary' : undefined}>{v}</Text>,
    },
    {
      title: 'NET',
      dataIndex: 'net',
      key: 'net',
      width: 120,
      align: 'right',
      render: (v) => v > 0 ? `£${v.toFixed(2)}` : <Text type="secondary">—</Text>,
    },
    {
      title: isOpcUser ? 'End of Day' : 'Total',
      dataIndex: 'total',
      key: 'total',
      width: 120,
      align: 'right',
      render: (v) => (
        <Text strong style={v > 0 ? { color: '#3f8600' } : {}}>
          {v > 0 ? `£${v.toFixed(2)}` : '—'}
        </Text>
      ),
    },
    {
      title: '',
      key: 'action',
      width: 100,
      align: 'right',
      render: (_, record) => (
        <Button size="small" onClick={() => onSelectDate(record.date)}>
          {record.count === 0 ? 'Start' : 'Open'}
        </Button>
      ),
    },
  ];

  const searchColumns = [
    { title: 'Date', dataIndex: 'date', key: 'date', render: (d) => dayjs(d).format('ddd DD MMM YYYY'), width: 150 },
    { title: 'Reference', dataIndex: 'reference', key: 'reference', render: (v) => v || <Text type="secondary">—</Text> },
    { title: 'Van Type', dataIndex: 'vanType', key: 'vanType', render: (v) => v || <Text type="secondary">—</Text> },
    { title: 'Pickup', dataIndex: 'pickupPostcode', key: 'pickup', render: (v) => v || <Text type="secondary">—</Text> },
    { title: 'Drop-off', dataIndex: 'dropOffPostcode', key: 'dropoff', render: (v) => v || <Text type="secondary">—</Text> },
    { title: 'Total', key: 'total', align: 'right', render: (_, r) => { const v = isOpcUser ? r.opcShare : r.total; return v ? `£${Number(v).toFixed(2)}` : '—'; } },
    { title: '', key: 'action', width: 80, render: (_, r) => <Button size="small" onClick={() => { handleClearSearch(); onSelectDate(dayjs(r.date).format('YYYY-MM-DD')); }}>Open</Button> },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <Space>
          <Title level={4} style={{ margin: 0 }}>Daily Contract Runs</Title>
          <DatePicker
            picker="month"
            value={viewMonth}
            onChange={(m) => m && setViewMonth(m)}
            format="MMM YYYY"
            disabledDate={(d) => d && d.isAfter(dayjs(), 'month')}
            allowClear={false}
          />
        </Space>
        <Space>
          <Button icon={<FileExcelOutlined />} onClick={handleExportMonth} loading={exportLoading}>
            Download Excel
          </Button>
          <DatePicker
            placeholder="Go to date"
            format="DD MMM YYYY"
            disabledDate={(d) => d && d.isAfter(dayjs(), 'day')}
            onChange={(d) => d && onSelectDate(d.format('YYYY-MM-DD'))}
            allowClear={false}
            value={null}
          />
        </Space>
      </div>

      {/* Search bar */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <Input
          placeholder="Postcode (pickup or drop-off)"
          value={searchPostcode}
          onChange={(e) => setSearchPostcode(e.target.value)}
          allowClear
          style={{ width: 220 }}
          suffix={searchLoading && searchPostcode ? <span style={{ fontSize: 11, color: '#aaa' }}>…</span> : null}
        />
        <Input
          placeholder="Reference"
          value={searchRef}
          onChange={(e) => setSearchRef(e.target.value)}
          allowClear
          style={{ width: 160 }}
          suffix={searchLoading && searchRef ? <span style={{ fontSize: 11, color: '#aaa' }}>…</span> : null}
        />
      </div>

      {searchResults !== null ? (
        <Table
          dataSource={searchResults}
          columns={searchColumns}
          rowKey="_id"
          loading={searchLoading}
          pagination={{ pageSize: 50, hideOnSinglePage: true }}
          size="small"
          locale={{ emptyText: 'No runs found' }}
        />
      ) : (
        <Table
          dataSource={dateSummaries}
          columns={columns}
          rowKey="date"
          loading={loading}
          pagination={false}
          size="small"
          onRow={(record) => ({ onClick: () => onSelectDate(record.date), style: { cursor: 'pointer' } })}
          style={{ borderRadius: 8, overflow: 'hidden' }}
        />
      )}
    </div>
  );
};

// ─── Day detail view ──────────────────────────────────────────────────────────

const DayDetailView = ({ date, onBack }) => {
  const { isOpcUser } = useAuth();
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);

  const [editingKey, setEditingKey] = useState(null);
  const [editTotal, setEditTotal] = useState(0);
  const [editVatTotal, setEditVatTotal] = useState(0);
  const [editIsExternal, setEditIsExternal] = useState(false);
  const [editForm] = Form.useForm();

  const [addTotal, setAddTotal] = useState(0);
  const [addVatTotal, setAddVatTotal] = useState(0);
  const [addIsExternal, setAddIsExternal] = useState(false);
  const [addForm] = Form.useForm();

  const [filterChannel, setFilterChannel] = useState(null);
  const [filterDriver, setFilterDriver] = useState(null);
  const [filterVan, setFilterVan] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [vans, setVans] = useState([]);
  const [clientOptions, setClientOptions] = useState([]);

  const [podModalRun, setPodModalRun] = useState(null);
  const [podList, setPodList] = useState([]);
  const [podLoading, setPodLoading] = useState(false);
  const [podUploading, setPodUploading] = useState(false);
  const pendingUploads = useRef(0);

  const firstFieldRef = useRef(null);

  const dayjsDate = dayjs(date);

  useEffect(() => {
    const fetchFleet = async () => {
      try {
        // scope=all lets our own users assign either side's drivers/vans when
        // creating runs. OPC users are still restricted to their own (enforced
        // server-side regardless of this param).
        const [drRes, vnRes] = await Promise.all([
          apiService.drivers.getAll({ scope: 'all' }),
          apiService.vans.getAll({ scope: 'all' }),
        ]);
        setDrivers(Array.isArray(drRes.data) ? drRes.data : drRes.data?.drivers || []);
        setVans(Array.isArray(vnRes.data) ? vnRes.data : vnRes.data?.vans || []);
      } catch {
        message.error('Failed to load drivers and vans');
      }
      try {
        const clRes = await apiService.opcClients.getAll();
        setClientOptions(Array.isArray(clRes.data) ? clRes.data.map((c) => c.name) : []);
      } catch {
        // non-fatal — client dropdown will be empty
      }
    };
    fetchFleet();
  }, []);

  const fetchRuns = useCallback(async () => {
    try {
      setLoading(true);
      const params = { date };
      if (filterChannel) params.channel = filterChannel;
      if (filterDriver) {
        const driverName = drivers.find(d => d._id === filterDriver)?.name || '';
        if (driverName.toLowerCase().startsWith('lite')) {
          params.liteCovered = true;
        } else {
          params.driver = filterDriver;
        }
      }
      if (filterVan) params.van = filterVan;
      const res = await apiService.dailyRuns.getAll(params);
      setRuns(res.data || []);
    } catch {
      message.error('Failed to fetch runs');
    } finally {
      setLoading(false);
    }
  }, [date, filterChannel, filterDriver, filterVan, drivers]);

  useEffect(() => {
    fetchRuns();
  }, [fetchRuns]);

  // ── Inline edit ─────────────────────────────────────────────────────────────

  const startEdit = (record) => {
    if (editingKey) cancelEdit();
    const driverObj = drivers.find(d => d._id === (record.driver?._id || record.driver));
    setEditIsExternal(driverObj?.isExternal || false);
    editForm.setFieldsValue({
      reference: record.reference,
      channel: record.channel,
      driver: record.driver?._id || undefined,
      van: record.van?._id || undefined,
      pickupTime: record.pickupTime,
      pickupPostcode: record.pickupPostcode,
      dropOffPostcode: record.dropOffPostcode,
      vanType: record.vanType,
      net: record.net,
      waitingTime: record.waitingTime,
      waitingCost: record.waitingCost,
      congestion: record.congestion,
      osp: record.osp || undefined,
      discount: record.discount || undefined,
      notes: record.notes || '',
    });
    setEditTotal(record.total || 0);
    setEditVatTotal(vatTotal(record.net, record.waitingCost, record.congestion, record.discount));
    setEditingKey(record._id);
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setEditIsExternal(false);
    editForm.resetFields();
  };

  const saveEdit = async (record) => {
    try {
      const values = await editForm.validateFields();
      await apiService.dailyRuns.update(record._id, { ...values, date: record.date });
      setEditingKey(null);
      message.success('Saved');
      fetchRuns();
    } catch (e) {
      if (e?.response?.data?.message) message.error(e.response.data.message);
    }
  };

  const vatTotal = (net, waitingCost, congestion, discount) =>
    +((((net || 0) + (waitingCost || 0) - (discount || 0)) * 1.2) + (congestion || 0)).toFixed(2);

  const recalcEdit = () => {
    const v = editForm.getFieldsValue(['net', 'waitingCost', 'congestion', 'discount']);
    setEditTotal(+((v.net || 0) + (v.waitingCost || 0) + (v.congestion || 0) - (v.discount || 0)).toFixed(2));
    setEditVatTotal(vatTotal(v.net, v.waitingCost, v.congestion, v.discount));
  };

  // ── Add row ──────────────────────────────────────────────────────────────────

  const recalcAdd = () => {
    const v = addForm.getFieldsValue(['net', 'waitingCost', 'congestion', 'discount']);
    setAddTotal(+((v.net || 0) + (v.waitingCost || 0) + (v.congestion || 0) - (v.discount || 0)).toFixed(2));
    setAddVatTotal(vatTotal(v.net, v.waitingCost, v.congestion, v.discount));
  };

  const handleAddRow = async () => {
    try {
      const values = await addForm.validateFields();
      const prevVanType = values.vanType;
      values.pickupPostcode = (values.pickupPostcode || '').toUpperCase();
      values.dropOffPostcode = (values.dropOffPostcode || '').toUpperCase();

      await apiService.dailyRuns.create({ ...values, date: `${date}T12:00:00.000Z` });

      addForm.resetFields();
      addForm.setFieldsValue({ vanType: prevVanType });
      setAddTotal(0);
      setAddVatTotal(0);
      setAddIsExternal(false);
      fetchRuns();
      setTimeout(() => firstFieldRef.current?.focus(), 100);
    } catch (e) {
      if (e?.response?.data?.message) message.error(e.response.data.message);
    }
  };

  // ── Delete ───────────────────────────────────────────────────────────────────

  const handleDelete = async (id) => {
    try {
      await apiService.dailyRuns.delete(id);
      message.success('Deleted');
      fetchRuns();
    } catch {
      message.error('Failed to delete');
    }
  };

  // ── Export ───────────────────────────────────────────────────────────────────

  const handleExport = async () => {
    try {
      const params = { date };
      if (filterChannel) params.channel = filterChannel;
      const res = await apiService.dailyRuns.export(params);
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const channelStr = filterChannel ? filterChannel.replace(/\s+/g, '-') : 'all';
      link.setAttribute('download', `daily-runs-${channelStr}-${dayjsDate.format('DD-MM-YYYY')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      message.error('Export failed');
    }
  };

  const openPodModal = async (run) => {
    setPodModalRun(run);
    setPodList([]);
    setPodLoading(true);
    try {
      const res = await apiService.dailyRuns.getPods(run._id);
      setPodList(res.data || []);
    } catch {
      message.error('Failed to load PODs');
    } finally {
      setPodLoading(false);
    }
  };

  const handlePodUpload = async ({ file }) => {
    const runId = podModalRun?._id;
    if (!runId) return;
    pendingUploads.current += 1;
    setPodUploading(true);
    try {
      await apiService.dailyRuns.uploadPod(runId, file);
    } catch {
      message.error(`Failed to upload ${file.name}`);
    } finally {
      pendingUploads.current -= 1;
      if (pendingUploads.current === 0) {
        setPodUploading(false);
        try {
          const res = await apiService.dailyRuns.getPods(runId);
          setPodList(res.data || []);
        } catch {}
        fetchRuns();
        message.success('POD(s) uploaded');
      }
    }
  };

  const handlePodDelete = async (podId) => {
    try {
      await apiService.dailyRuns.deletePod(podModalRun._id, podId);
      setPodList((prev) => prev.filter((p) => p._id !== podId));
      fetchRuns();
    } catch {
      message.error('Delete failed');
    }
  };

  const isEditing = (record) => record._id === editingKey;
  const totalNet = runs.reduce((s, r) => s + (r.net || 0), 0);
  // For OPC, the end-of-day total counts Lite-covered runs at 15% (opcShare);
  // own-driver runs at full. For our staff, opcShare equals the full total.
  const grandTotal = runs.reduce((s, r) => s + (r.opcShare ?? r.total ?? 0), 0);
  const grossTotal = runs.reduce((s, r) => s + (r.total || 0), 0);
  const filteredDrivers = drivers
    .filter(dr => !EXCLUDED_DRIVERS.some(ex => dr.name?.toLowerCase().includes(ex)))
    // OPC drivers first, then alphabetically within each group.
    .sort((a, b) => {
      const ao = a.org === 'opc' ? 0 : 1;
      const bo = b.org === 'opc' ? 0 : 1;
      return ao - bo || (a.name || '').localeCompare(b.name || '');
    });

  // ── Columns ──────────────────────────────────────────────────────────────────

  const columns = [
    {
      title: 'Client',
      dataIndex: 'channel',
      key: 'channel',
      width: 160,
      render: (v, record) =>
        isEditing(record) ? (
          <Form.Item name="channel" noStyle rules={[{ required: true, message: ' ' }]}>
            <Select size="small" style={{ width: 150 }} showSearch placeholder="Client">
              {clientOptions.map((c) => <Option key={c} value={c}>{c}</Option>)}
            </Select>
          </Form.Item>
        ) : (
          v || <Text type="secondary">—</Text>
        ),
    },
    {
      title: 'Ref',
      dataIndex: 'reference',
      key: 'reference',
      width: 120,
      render: (v, record) =>
        isEditing(record) ? (
          <Form.Item name="reference" noStyle>
            <Input size="small" style={{ width: 112 }} placeholder="Reference" />
          </Form.Item>
        ) : (
          v || <Text type="secondary">—</Text>
        ),
    },
    {
      title: 'Driver',
      dataIndex: 'driver',
      key: 'driver',
      width: 148,
      render: (d, record) =>
        isEditing(record) ? (
          <Form.Item name="driver" noStyle>
            <Select
              size="small" style={{ width: 140 }} allowClear showSearch placeholder="Driver"
              onChange={(val) => {
                const dr = drivers.find(d => d._id === val);
                setEditIsExternal(dr?.isExternal || false);
                if (!dr?.isExternal) editForm.setFieldValue('osp', undefined);
              }}
            >
              {filteredDrivers.map((dr) => (
                <Option key={dr._id} value={dr._id}>{dr.name}{dr.isExternal ? ' (Ext)' : ''}</Option>
              ))}
            </Select>
          </Form.Item>
        ) : d ? (
          <span>{d.name}{d.isExternal ? <Tag color="orange" style={{ marginLeft: 4, fontSize: 10, padding: '0 4px' }}>Ext</Tag> : null}</span>
        ) : <Text type="secondary">—</Text>,
    },
    {
      title: 'Van',
      dataIndex: 'van',
      key: 'van',
      width: 130,
      render: (vn, record) =>
        isEditing(record) ? (
          <Form.Item name="van" noStyle>
            <Select size="small" style={{ width: 122 }} allowClear showSearch placeholder="Van" optionFilterProp="children">
              {vans.map((v) => (
                <Option key={v._id} value={v._id}>{v.registration}</Option>
              ))}
            </Select>
          </Form.Item>
        ) : vn ? vn.registration : <Text type="secondary">—</Text>,
    },
    {
      title: 'Time',
      dataIndex: 'pickupTime',
      key: 'pickupTime',
      width: 96,
      render: (v, record) =>
        isEditing(record) ? (
          <Form.Item name="pickupTime" noStyle rules={[{ required: true, message: ' ' }]}>
            <Select size="small" style={{ width: 88 }} showSearch placeholder="HH:MM">
              {TIME_OPTIONS.map((t) => <Option key={t} value={t}>{t}</Option>)}
            </Select>
          </Form.Item>
        ) : v,
    },
    {
      title: 'Pick Up',
      dataIndex: 'pickupPostcode',
      key: 'pickupPostcode',
      width: 96,
      render: (v, record) =>
        isEditing(record) ? (
          <Form.Item name="pickupPostcode" noStyle rules={[{ required: true, message: ' ' }]}>
            <Input size="small" style={{ width: 88, textTransform: 'uppercase' }} placeholder="Postcode" />
          </Form.Item>
        ) : v,
    },
    {
      title: 'Drop Off',
      dataIndex: 'dropOffPostcode',
      key: 'dropOffPostcode',
      width: 96,
      render: (v, record) =>
        isEditing(record) ? (
          <Form.Item name="dropOffPostcode" noStyle rules={[{ required: true, message: ' ' }]}>
            <Input size="small" style={{ width: 88, textTransform: 'uppercase' }} placeholder="Postcode" />
          </Form.Item>
        ) : v,
    },
    {
      title: 'Type',
      dataIndex: 'vanType',
      key: 'vanType',
      width: 138,
      render: (v, record) =>
        isEditing(record) ? (
          <Form.Item name="vanType" noStyle rules={[{ required: true, message: ' ' }]}>
            <Select size="small" style={{ width: 130 }} placeholder="Van type">
              {VAN_TYPES.map((vt) => <Option key={vt} value={vt}>{vt}</Option>)}
            </Select>
          </Form.Item>
        ) : v,
    },
    {
      title: 'NET',
      dataIndex: 'net',
      key: 'net',
      width: 88,
      align: 'right',
      render: (v, record) =>
        isEditing(record) ? (
          <Form.Item name="net" noStyle>
            <InputNumber size="small" style={{ width: 80 }} min={0} precision={2} onChange={recalcEdit} />
          </Form.Item>
        ) : `£${(v || 0).toFixed(2)}`,
    },
    {
      title: 'W/T',
      dataIndex: 'waitingTime',
      key: 'waitingTime',
      width: 66,
      align: 'right',
      render: (v, record) =>
        isEditing(record) ? (
          <Form.Item name="waitingTime" noStyle>
            <InputNumber size="small" style={{ width: 58 }} min={0} placeholder="mins" />
          </Form.Item>
        ) : v ? `${v}m` : '—',
    },
    {
      title: 'W/C',
      dataIndex: 'waitingCost',
      key: 'waitingCost',
      width: 82,
      align: 'right',
      render: (v, record) =>
        isEditing(record) ? (
          <Form.Item name="waitingCost" noStyle>
            <InputNumber size="small" style={{ width: 74 }} min={0} precision={2} onChange={recalcEdit} />
          </Form.Item>
        ) : v ? `£${v.toFixed(2)}` : '—',
    },
    {
      title: 'L&C',
      dataIndex: 'congestion',
      key: 'congestion',
      width: 82,
      align: 'right',
      render: (v, record) =>
        isEditing(record) ? (
          <Form.Item name="congestion" noStyle>
            <InputNumber size="small" style={{ width: 74 }} min={0} precision={2} onChange={recalcEdit} />
          </Form.Item>
        ) : v ? `£${v.toFixed(2)}` : '—',
    },
    {
      title: 'OSP',
      key: 'osp',
      dataIndex: 'osp',
      width: 90,
      align: 'right',
      render: (v, record) => {
        if (isEditing(record)) {
          return editIsExternal ? (
            <Form.Item name="osp" noStyle>
              <InputNumber size="small" style={{ width: 74 }} min={0} precision={2} placeholder="0.00" />
            </Form.Item>
          ) : <Text type="secondary">—</Text>;
        }
        return v ? <Text style={{ color: '#fa541c' }}>£{v.toFixed(2)}</Text> : <Text type="secondary">—</Text>;
      },
    },
    {
      title: 'Disc',
      dataIndex: 'discount',
      key: 'discount',
      width: 82,
      align: 'right',
      render: (v, record) =>
        isEditing(record) ? (
          <Form.Item name="discount" noStyle>
            <InputNumber size="small" style={{ width: 74 }} min={0} precision={2} placeholder="0.00" onChange={recalcEdit} />
          </Form.Item>
        ) : v ? <Text style={{ color: '#fa541c' }}>-£{v.toFixed(2)}</Text> : <Text type="secondary">—</Text>,
    },
    {
      title: isOpcUser ? 'Your Total' : 'Total',
      dataIndex: 'total',
      key: 'total',
      width: 104,
      align: 'right',
      render: (v, record) => {
        if (isEditing(record)) {
          return <Text strong style={{ color: '#3f8600' }}>£{editTotal.toFixed(2)}</Text>;
        }
        // OPC view: show their share with % hint (15% for Lite-covered, 85% for CX).
        if (isOpcUser && record.coveredByLite) {
          const pct = record.isCxJob ? '85%' : '15%';
          return (
            <div style={{ lineHeight: 1.2 }}>
              <Text strong style={{ color: '#3f8600' }}>£{(record.opcShare ?? 0).toFixed(2)}</Text>
              <div style={{ fontSize: 10, color: '#999' }}>{pct} of £{(v || 0).toFixed(2)}</div>
            </div>
          );
        }
        // External driver: show net after OSP deduction.
        if (isOpcUser && record.isExternalJob) {
          return <Text strong style={{ color: '#3f8600' }}>£{(record.opcShare ?? 0).toFixed(2)}</Text>;
        }
        // Our admin view: CX client jobs — we keep 15%, show OPC's 85% as a hint.
        if (!isOpcUser && record.isCxJob) {
          const opcAmt = ((v || 0) * 0.85).toFixed(2);
          return (
            <div style={{ lineHeight: 1.2 }}>
              <Text strong>£{(v || 0).toFixed(2)}</Text>
              <div style={{ fontSize: 10, color: '#999' }}>OPC 85% · £{opcAmt}</div>
            </div>
          );
        }
        return <Text strong>£{(v || 0).toFixed(2)}</Text>;
      },
    },
    {
      title: 'Total + VAT',
      key: 'vatTotal',
      width: 110,
      align: 'right',
      render: (_, record) => {
        if (isEditing(record)) {
          return <Text strong style={{ color: '#1677ff' }}>£{editVatTotal.toFixed(2)}</Text>;
        }
        const vt = vatTotal(record.net, record.waitingCost, record.congestion, record.discount);
        return <Text style={{ color: '#1677ff' }}>£{vt.toFixed(2)}</Text>;
      },
    },
    {
      title: 'POD',
      key: 'pods',
      width: 54,
      align: 'center',
      render: (_, record) => {
        const count = record.podCount || 0;
        return (
          <Tooltip title={count > 0 ? `${count} POD${count !== 1 ? 's' : ''} attached` : 'Attach POD'}>
            <Badge count={count} size="small" offset={[-2, 2]} style={{ backgroundColor: '#52c41a' }}>
              <Button
                type="text"
                size="small"
                icon={<PaperClipOutlined />}
                onClick={() => openPodModal(record)}
              />
            </Badge>
          </Tooltip>
        );
      },
    },
    {
      title: 'Notes',
      dataIndex: 'notes',
      key: 'notes',
      width: 140,
      render: (v, record) =>
        isEditing(record) ? (
          <Form.Item name="notes" noStyle>
            <Input size="small" style={{ width: 130 }} placeholder="Notes" />
          </Form.Item>
        ) : v ? (
          <Tooltip title={v}>
            <Text type="secondary" style={{ fontSize: 12, cursor: 'default' }} ellipsis>
              {v.length > 20 ? v.slice(0, 20) + '…' : v}
            </Text>
          </Tooltip>
        ) : <Text type="secondary">—</Text>,
    },
    {
      title: '',
      key: 'actions',
      width: 72,
      render: (_, record) =>
        isEditing(record) ? (
          <Space size={2}>
            <Button type="text" size="small" icon={<CheckOutlined style={{ color: '#3f8600' }} />} onClick={() => saveEdit(record)} />
            <Button type="text" size="small" icon={<CloseOutlined />} onClick={cancelEdit} />
          </Space>
        ) : (
          <Space size={2}>
            <Button type="text" size="small" icon={<EditOutlined />} onClick={() => startEdit(record)} />
            <Popconfirm title="Are you sure you want to delete this run?" onConfirm={() => handleDelete(record._id)} okText="Yes" cancelText="No" okButtonProps={{ danger: true }}>
              <Button type="text" size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        ),
    },
  ];

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={onBack} />
          <CalendarOutlined style={{ fontSize: 16 }} />
          <Title level={4} style={{ margin: 0 }}>{dayjsDate.format('ddd DD MMM YYYY')}</Title>
        </Space>
        <Button icon={<FileExcelOutlined />} onClick={handleExport} disabled={runs.length === 0}>
          Download Excel
        </Button>
      </div>

      {/* Filters */}
      <Card size="small" style={{ marginBottom: 12 }}>
        <Row gutter={16} align="middle" wrap>
          <Col>
            <Text strong style={{ marginRight: 8 }}>Client</Text>
            <Select
              placeholder="All clients"
              value={filterChannel}
              onChange={setFilterChannel}
              style={{ width: 180 }}
              showSearch
              allowClear
            >
              {clientOptions.map((c) => <Option key={c} value={c}>{c}</Option>)}
            </Select>
          </Col>
          <Col>
            <Text strong style={{ marginRight: 8 }}>Driver</Text>
            <Select
              placeholder="All drivers"
              value={filterDriver}
              onChange={setFilterDriver}
              style={{ width: 180 }}
              showSearch
              allowClear
              optionFilterProp="children"
            >
              {filteredDrivers.map((dr) => <Option key={dr._id} value={dr._id}>{dr.name}</Option>)}
            </Select>
          </Col>
          <Col>
            <Text strong style={{ marginRight: 8 }}>Van</Text>
            <Select
              placeholder="All vans"
              value={filterVan}
              onChange={setFilterVan}
              style={{ width: 160 }}
              showSearch
              allowClear
              optionFilterProp="children"
            >
              {vans.map((v) => <Option key={v._id} value={v._id}>{v.registration}</Option>)}
            </Select>
          </Col>
          {runs.length > 0 && (
            <>
              <Col flex="auto" />
              <Col>
                <Text type="secondary" style={{ marginRight: 16 }}>
                  {runs.length} run{runs.length !== 1 ? 's' : ''}
                </Text>
                <Text style={{ marginRight: 16 }}>
                  NET: <Text strong style={{ fontVariantNumeric: 'tabular-nums' }}>£{totalNet.toFixed(2)}</Text>
                </Text>
                {isOpcUser && grossTotal !== grandTotal && (
                  <Text type="secondary" style={{ marginRight: 16 }}>
                    Job value: <Text style={{ fontVariantNumeric: 'tabular-nums' }}>£{grossTotal.toFixed(2)}</Text>
                  </Text>
                )}
                <Text>
                  {isOpcUser ? 'End of Day' : 'Total'}:{' '}
                  <Text strong style={{ color: '#3f8600', fontSize: 15, fontVariantNumeric: 'tabular-nums' }}>
                    £{grandTotal.toFixed(2)}
                  </Text>
                </Text>
              </Col>
            </>
          )}
        </Row>
      </Card>

      {/* Add row */}
      <Card
        size="small"
        style={{ marginBottom: 12, borderTop: `2px solid ${COLOR_PRIMARY}`, background: 'rgba(22,119,255,0.06)' }}
        bodyStyle={{ padding: '10px 12px' }}
      >
        <Form form={addForm} onFinish={handleAddRow}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>

            <div>
              <div style={labelStyle}>Client *</div>
              <Form.Item name="channel" noStyle rules={[{ required: true, message: ' ' }]}>
                <Select ref={firstFieldRef} size="small" style={{ width: 150 }} showSearch placeholder="Client">
                  {clientOptions.map((c) => <Option key={c} value={c}>{c}</Option>)}
                </Select>
              </Form.Item>
            </div>

            <div>
              <div style={labelStyle}>Ref</div>
              <Form.Item name="reference" noStyle>
                <Input size="small" style={{ width: 120 }} placeholder="Reference" />
              </Form.Item>
            </div>

            <div>
              <div style={labelStyle}>Driver</div>
              <Form.Item name="driver" noStyle>
                <Select
                  size="small" style={{ width: 140 }} allowClear showSearch placeholder="Driver"
                  onChange={(val) => {
                    const dr = drivers.find(d => d._id === val);
                    setAddIsExternal(dr?.isExternal || false);
                    if (!dr?.isExternal) addForm.setFieldValue('osp', undefined);
                  }}
                >
                  {filteredDrivers.map((dr) => (
                    <Option key={dr._id} value={dr._id}>{dr.name}{dr.isExternal ? ' (Ext)' : ''}</Option>
                  ))}
                </Select>
              </Form.Item>
            </div>

            <div>
              <div style={labelStyle}>Van</div>
              <Form.Item name="van" noStyle>
                <Select size="small" style={{ width: 122 }} allowClear showSearch placeholder="Van" optionFilterProp="children">
                  {vans.map((v) => (
                    <Option key={v._id} value={v._id}>{v.registration}</Option>
                  ))}
                </Select>
              </Form.Item>
            </div>

            <div>
              <div style={labelStyle}>Time *</div>
              <Form.Item name="pickupTime" noStyle rules={[{ required: true, message: ' ' }]}>
                <Select size="small" style={{ width: 96 }} showSearch placeholder="HH:MM">
                  {TIME_OPTIONS.map((t) => <Option key={t} value={t}>{t}</Option>)}
                </Select>
              </Form.Item>
            </div>

            <div>
              <div style={labelStyle}>Pick Up *</div>
              <Form.Item name="pickupPostcode" noStyle rules={[{ required: true, message: ' ' }]}>
                <Input
                  size="small"
                  style={{ width: 90 }}
                  placeholder="Postcode"
                  onBlur={(e) => addForm.setFieldValue('pickupPostcode', e.target.value.toUpperCase())}
                />
              </Form.Item>
            </div>

            <div>
              <div style={labelStyle}>Drop Off *</div>
              <Form.Item name="dropOffPostcode" noStyle rules={[{ required: true, message: ' ' }]}>
                <Input
                  size="small"
                  style={{ width: 90 }}
                  placeholder="Postcode"
                  onBlur={(e) => addForm.setFieldValue('dropOffPostcode', e.target.value.toUpperCase())}
                />
              </Form.Item>
            </div>

            <div>
              <div style={labelStyle}>Type *</div>
              <Form.Item name="vanType" noStyle rules={[{ required: true, message: ' ' }]}>
                <Select size="small" style={{ width: 130 }} placeholder="Van type">
                  {VAN_TYPES.map((vt) => <Option key={vt} value={vt}>{vt}</Option>)}
                </Select>
              </Form.Item>
            </div>

            <div>
              <div style={labelStyle}>NET (£)</div>
              <Form.Item name="net" noStyle>
                <InputNumber size="small" style={{ width: 82 }} min={0} precision={2} placeholder="0.00" onChange={recalcAdd} />
              </Form.Item>
            </div>

            <div>
              <div style={labelStyle}>W/T (m)</div>
              <Form.Item name="waitingTime" noStyle>
                <InputNumber size="small" style={{ width: 62 }} min={0} placeholder="0" />
              </Form.Item>
            </div>

            <div>
              <div style={labelStyle}>W/C (£)</div>
              <Form.Item name="waitingCost" noStyle>
                <InputNumber size="small" style={{ width: 76 }} min={0} precision={2} placeholder="0.00" onChange={recalcAdd} />
              </Form.Item>
            </div>

            <div>
              <div style={labelStyle}>L&amp;C (£)</div>
              <Form.Item name="congestion" noStyle>
                <InputNumber size="small" style={{ width: 76 }} min={0} precision={2} placeholder="0.00" onChange={recalcAdd} onPressEnter={handleAddRow} />
              </Form.Item>
            </div>

            {addIsExternal && (
              <div>
                <div style={labelStyle}>OSP (£)</div>
                <Form.Item name="osp" noStyle>
                  <InputNumber size="small" style={{ width: 76 }} min={0} precision={2} placeholder="0.00" />
                </Form.Item>
              </div>
            )}

            <div>
              <div style={labelStyle}>Disc (£)</div>
              <Form.Item name="discount" noStyle>
                <InputNumber size="small" style={{ width: 76 }} min={0} precision={2} placeholder="0.00" onChange={recalcAdd} />
              </Form.Item>
            </div>

            <div>
              <div style={labelStyle}>Total</div>
              <div style={{
                width: 82, height: 24, lineHeight: '24px', fontSize: 13, fontWeight: 700,
                color: '#3f8600', background: '#f6ffed', border: '1px solid #b7eb8f',
                borderRadius: 4, padding: '0 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums',
              }}>
                £{addTotal.toFixed(2)}
              </div>
            </div>

            <div>
              <div style={labelStyle}>+ VAT</div>
              <div style={{
                width: 90, height: 24, lineHeight: '24px', fontSize: 13, fontWeight: 700,
                color: '#1677ff', background: '#e6f4ff', border: '1px solid #91caff',
                borderRadius: 4, padding: '0 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums',
              }}>
                £{addVatTotal.toFixed(2)}
              </div>
            </div>

            <div style={{ alignSelf: 'flex-end' }}>
              <Button type="primary" size="small" htmlType="submit" icon={<PlusOutlined />}>Add</Button>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 6, alignItems: 'flex-end' }}>
            <div style={{ flex: 1 }}>
              <div style={labelStyle}>Notes</div>
              <Form.Item name="notes" noStyle>
                <Input size="small" style={{ width: '100%' }} placeholder="Optional notes" />
              </Form.Item>
            </div>
          </div>
          <div style={{ marginTop: 5, color: '#888', fontSize: 11 }}>
            Tab through fields · Enter or click Add to save · Van type carries forward
          </div>
        </Form>
      </Card>

      {/* POD modal */}
      <Modal
        title={
          podModalRun
            ? `PODs — ${podModalRun.reference || podModalRun.channel || 'Run'} · ${dayjs(podModalRun.date).format('DD MMM YYYY')}`
            : 'PODs'
        }
        open={!!podModalRun}
        onCancel={() => setPodModalRun(null)}
        footer={null}
        width={520}
      >
        <Upload
          accept="image/*,application/pdf"
          showUploadList={false}
          customRequest={handlePodUpload}
          disabled={podUploading}
          multiple
        >
          <Button icon={<UploadOutlined />} loading={podUploading} style={{ marginBottom: 16 }}>
            Upload POD(s)
          </Button>
        </Upload>

        {podLoading ? (
          <div style={{ textAlign: 'center', padding: 24, color: '#999' }}>Loading…</div>
        ) : podList.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 24, color: '#999' }}>No PODs attached yet</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {podList.map((pod, i) => {
              const isImage = pod.contentType?.startsWith('image/');
              return (
                <div
                  key={pod._id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    border: '1px solid #f0f0f0', borderRadius: 6, padding: '8px 10px',
                  }}
                >
                  {isImage ? (
                    <Image
                      src={pod.url}
                      width={48} height={48}
                      style={{ objectFit: 'cover', borderRadius: 4, flexShrink: 0 }}
                      preview={{ src: pod.url }}
                    />
                  ) : (
                    <div style={{ width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fafafa', borderRadius: 4, flexShrink: 0 }}>
                      <FilePdfIcon style={{ fontSize: 24, color: '#ff4d4f' }} />
                    </div>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>POD {i + 1}</div>
                    <div style={{ fontSize: 11, color: '#999' }}>{dayjs(pod.createdAt).format('DD MMM YYYY HH:mm')}</div>
                  </div>
                  <Space size={4}>
                    <Tooltip title="View / Download">
                      <Button
                        type="text" size="small" icon={<EyeOutlined />}
                        href={pod.url} target="_blank" rel="noopener noreferrer"
                      />
                    </Tooltip>
                    <Popconfirm title="Remove this POD?" onConfirm={() => handlePodDelete(pod._id)} okText="Yes" cancelText="No" okButtonProps={{ danger: true }}>
                      <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                  </Space>
                </div>
              );
            })}
          </div>
        )}
      </Modal>

      {/* Runs table */}
      <Form form={editForm} component={false}>
        <Table
          dataSource={runs}
          columns={columns}
          rowKey="_id"
          loading={loading}
          pagination={false}
          size="small"
          scroll={{ x: 1530 }}
          style={{ borderRadius: 8, overflow: 'hidden' }}
          locale={{ emptyText: 'No runs yet — fill in the form above to add one' }}
          rowClassName={(record) => isEditing(record) ? 'daily-run-editing-row' : ''}
          summary={
            runs.length > 0
              ? () => (
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={8}>
                      <Text type="secondary" style={{ fontSize: 12 }}>Totals</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={8} align="right">
                      <Text strong>£{totalNet.toFixed(2)}</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={9} />
                    <Table.Summary.Cell index={10} align="right">
                      <Text strong>£{runs.reduce((s, r) => s + (r.waitingCost || 0), 0).toFixed(2)}</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={11} align="right">
                      <Text strong>£{runs.reduce((s, r) => s + (r.congestion || 0), 0).toFixed(2)}</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={12} align="right">
                      {runs.some(r => r.osp) && (
                        <Text strong style={{ color: '#fa541c' }}>
                          £{runs.reduce((s, r) => s + (r.osp || 0), 0).toFixed(2)}
                        </Text>
                      )}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={13} align="right">
                      <Text strong style={{ color: '#3f8600' }}>£{grandTotal.toFixed(2)}</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={14} align="right">
                      <Text strong style={{ color: '#1677ff' }}>
                        £{runs.reduce((s, r) => s + vatTotal(r.net, r.waitingCost, r.congestion, r.discount), 0).toFixed(2)}
                      </Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={15} />
                  </Table.Summary.Row>
                )
              : undefined
          }
        />
      </Form>
    </div>
  );
};

// ─── Root component ───────────────────────────────────────────────────────────

const DailyRunList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedDate = searchParams.get('date') || null;

  const handleSelectDate = (date) => setSearchParams({ date });
  const handleBack = () => setSearchParams({});

  if (selectedDate) {
    return <DayDetailView date={selectedDate} onBack={handleBack} />;
  }
  return <DateListView onSelectDate={handleSelectDate} />;
};

const labelStyle = { fontSize: 11, color: '#888', marginBottom: 2 };

export default DailyRunList;
